/**
 * useLoopDiff — reads what changed since the last pass of the same ball.
 *
 * One call to the database function `catchball_loop_diff` (migration 822).
 * Pass a proposal id to compare that pass, a turn number to compare that turn,
 * or neither for the loop's latest pass. `diff.reference` is null when there is
 * nothing earlier to compare with; the block that shows it then stays hidden.
 */

import { useEffect, useState } from 'react';
import { isSupabaseAvailable } from '../lib/supabaseSync';
import { getTypedSupabaseClient } from '../lib/typedSupabaseClient';
import type { LoopDiff, LoopDiffResult } from '../types/prioritization/loopDiff';

export interface UseLoopDiffArgs {
  loopId: string | null | undefined;
  turn?: number | null;
  proposalId?: string | null;
  /** Skip the read entirely (for example while the loop is still loading). */
  enabled?: boolean;
}

export interface UseLoopDiffState {
  diff: LoopDiff | null;
  isLoading: boolean;
  error: string | null;
}

export function useLoopDiff({ loopId, turn, proposalId, enabled = true }: UseLoopDiffArgs): UseLoopDiffState {
  const [state, setState] = useState<UseLoopDiffState>({ diff: null, isLoading: Boolean(loopId && enabled), error: null });

  useEffect(() => {
    if (!loopId || !enabled) {
      setState({ diff: null, isLoading: false, error: null });
      return;
    }
    if (!isSupabaseAvailable()) {
      setState({ diff: null, isLoading: false, error: null });
      return;
    }
    // Typed client: schema drift in catchball_loop_diff is a compile error (CLAUDE.md, DB contract).
    const supabase = getTypedSupabaseClient();
    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    supabase
      .rpc('catchball_loop_diff', {
        p_loop_id: loopId,
        p_turn: turn ?? undefined,
        p_proposal_id: proposalId ?? undefined,
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setState({ diff: null, isLoading: false, error: error.message });
          return;
        }
        const result = (data ?? null) as unknown as LoopDiffResult | null;
        if (!result || result.success !== true) {
          setState({ diff: null, isLoading: false, error: result && 'error' in result ? result.error : null });
          return;
        }
        setState({ diff: result, isLoading: false, error: null });
      });
    return () => {
      cancelled = true;
    };
  }, [loopId, turn, proposalId, enabled]);

  return state;
}
